import React, { useEffect, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { FileText } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import {
  CandidateAffidavitType,
  renderCandidateAffidavitPdf,
} from './affidavitPdf';

const CANDIDATE_AFFIDAVITS = gql`
  query CandidateAffidavits($candidateId: Int!) {
    candidateAffidavits(candidateId: $candidateId) {
      id
      candidateId
      affidavitType
      templateVersion
      fileName
      generatedAt
      generatedByUsername
      sentAt
      sentByUsername
    }
  }
`;

const GENERATE_CANDIDATE_AFFIDAVIT = gql`
  mutation GenerateCandidateAffidavit($candidateId: Int!, $affidavitType: String!, $pdfBase64: String!) {
    generateCandidateAffidavit(
      candidateId: $candidateId
      affidavitType: $affidavitType
      pdfBase64: $pdfBase64
    ) {
      id
      candidateId
      affidavitType
      templateVersion
      fileName
      generatedAt
      generatedByUsername
      sentAt
      sentByUsername
    }
  }
`;

const SAVE_CANDIDATE_AFFIDAVIT_FIELDS = gql`
  mutation SaveCandidateAffidavitFields($candidateId: Int!, $diagnosis: String!, $nextCourtDate: String!) {
    updateCandidate(component: "odr", id: $candidateId, values: [{ key: "dx", value: $diagnosis }, { key: "next_court_date", value: $nextCourtDate }]) {
      success
      id
    }
  }
`;

const DOWNLOAD_CANDIDATE_AFFIDAVIT = gql`
  query DownloadCandidateAffidavit($affidavitId: Int!) {
    downloadCandidateAffidavit(affidavitId: $affidavitId) {
      fileName
      mimeType
      base64
    }
  }
`;

const MARK_CANDIDATE_AFFIDAVIT_SENT = gql`
  mutation MarkCandidateAffidavitSent($affidavitId: Int!, $sentDate: String) {
    markCandidateAffidavitSent(affidavitId: $affidavitId, sentDate: $sentDate) {
      id
      sentAt
      sentByUsername
    }
  }
`;

const SEND_CANDIDATE_AFFIDAVIT_EMAIL = gql`
  mutation SendCandidateAffidavitEmail($affidavitId: Int!, $recipient: String!) {
    sendCandidateAffidavitEmail(affidavitId: $affidavitId, recipient: $recipient) {
      id
      sentAt
      sentByUsername
    }
  }
`;

type CandidateAffidavit = {
  id: number;
  affidavitType: CandidateAffidavitType;
  templateVersion?: string | null;
  fileName: string;
  generatedAt?: string | null;
  generatedByUsername?: string | null;
  sentAt?: string | null;
  sentByUsername?: string | null;
};

type OdrCandidateAffidavitsProps = {
  candidateId: number;
  candidate: Record<string, unknown>;
  onChanged?: () => void | Promise<void>;
};

const normalizedBoolean = (value: unknown): boolean =>
  ['1', 'true', 'y', 'yes'].includes(String(value ?? '').trim().toLowerCase());

const localIsoDate = (): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const downloadBase64 = (base64: string, mimeType: string, fileName: string) => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeType || 'application/pdf' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName || 'candidate-affidavit.pdf';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export const OdrCandidateAffidavits = ({
  candidateId,
  candidate,
  onChanged,
}: OdrCandidateAffidavitsProps) => {
  const apolloClient = useApolloClient();
  const [affidavitType, setAffidavitType] = useState<CandidateAffidavitType>(
    normalizedBoolean(candidate.odr_mhd) ? 'mhd' : 'odrh',
  );
  const [generating, setGenerating] = useState(false);
  const [needsRequiredFields, setNeedsRequiredFields] = useState(false);
  const [diagnosis, setDiagnosis] = useState('');
  const [nextCourtDate, setNextCourtDate] = useState('');
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [sentAffidavit, setSentAffidavit] = useState<CandidateAffidavit | null>(null);
  const [sentDate, setSentDate] = useState(localIsoDate());
  const [recipient, setRecipient] = useState('');
  const { data, loading, refetch } = useQuery(CANDIDATE_AFFIDAVITS, {
    variables: { candidateId },
    skip: !candidateId,
    fetchPolicy: 'network-only',
  });
  const [retainAffidavit] = useMutation(GENERATE_CANDIDATE_AFFIDAVIT);
  const [saveAffidavitFields] = useMutation<{ updateCandidate: { success?: boolean | null } }>(SAVE_CANDIDATE_AFFIDAVIT_FIELDS);
  const [markSent, { loading: markingSent }] = useMutation(MARK_CANDIDATE_AFFIDAVIT_SENT);
  const [sendEmail, { loading: sendingEmail }] = useMutation(SEND_CANDIDATE_AFFIDAVIT_EMAIL);
  const affidavits: CandidateAffidavit[] = Array.isArray(data?.candidateAffidavits)
    ? data.candidateAffidavits
    : [];

  useEffect(() => {
    setAffidavitType(normalizedBoolean(candidate.odr_mhd) ? 'mhd' : 'odrh');
  }, [candidate.odr_mhd, candidateId]);

  useEffect(() => {
    setDiagnosis(String(candidate.dx ?? ''));
    setNextCourtDate(String(candidate.next_court_date ?? ''));
  }, [candidateId, candidate.dx, candidate.next_court_date]);

  useEffect(() => {
    setNeedsRequiredFields(false);
    setRecipient('');
  }, [candidateId]);

  const handleGenerate = async () => {
    if (!candidateId || generating) return;
    if (needsRequiredFields && (!diagnosis.trim() || !nextCourtDate)) {
      toast.error('Enter Diagnosis and Next Court Date before generating the affidavit.');
      return;
    }
    setGenerating(true);
    try {
      let affidavitCandidate = candidate;
      // The server validates saved candidate fields, so persist corrections before rendering and retention.
      if (needsRequiredFields) {
        const saved = await saveAffidavitFields({ variables: { candidateId, diagnosis: diagnosis.trim(), nextCourtDate } });
        if (!saved.data?.updateCandidate?.success) throw new Error('Diagnosis and Next Court Date were not saved.');
        affidavitCandidate = { ...candidate, dx: diagnosis.trim(), next_court_date: nextCourtDate };
      }
      const pdfBase64 = await renderCandidateAffidavitPdf(affidavitCandidate, affidavitType);
      await retainAffidavit({ variables: { candidateId, affidavitType, pdfBase64 } });
      await refetch();
      await onChanged?.();
      toast.success('Affidavit generated and retained.');
    } catch (error: unknown) {
      if (!String(candidate.dx ?? '').trim() || !String(candidate.next_court_date ?? '').trim()) {
        setNeedsRequiredFields(true);
      }
      toast.error(error instanceof Error ? error.message : 'Failed to generate affidavit.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (affidavit: CandidateAffidavit) => {
    setDownloadingId(affidavit.id);
    try {
      const response = await apolloClient.query({
        query: DOWNLOAD_CANDIDATE_AFFIDAVIT,
        variables: { affidavitId: affidavit.id },
        fetchPolicy: 'network-only',
      });
      const download = response.data?.downloadCandidateAffidavit;
      if (!download?.base64) throw new Error('The affidavit PDF was empty.');
      downloadBase64(download.base64, download.mimeType, download.fileName || affidavit.fileName);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to download affidavit.');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleMarkSent = async () => {
    if (!sentAffidavit) return;
    try {
      await markSent({ variables: { affidavitId: sentAffidavit.id, sentDate } });
      setSentAffidavit(null);
      await refetch();
      await onChanged?.();
      toast.success('Affidavit marked sent.');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to mark affidavit sent.');
    }
  };

  const handleSendEmail = async (affidavit: CandidateAffidavit) => {
    const email = recipient.trim();
    if (!email) {
      toast.error('Enter the affidavit recipient email.');
      return;
    }
    try {
      await sendEmail({ variables: { affidavitId: affidavit.id, recipient: email } });
      await refetch();
      await onChanged?.();
      toast.success('Affidavit email queued.');
    } catch (error: any) {
      toast.error(error?.message || 'Failed to send the affidavit email.');
    }
  };

  return (
    <section className="mt-5 border-t border-[var(--neutral-gray-40)] pt-4">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[var(--neutral-gray-120)]">Affidavits</h3>
          <p className="text-xs text-[var(--neutral-gray-100)]">
            The browser renders the PDF; the server retains the generated version.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1 text-xs font-medium text-[var(--neutral-gray-100)]">
            Affidavit template
            <select
              value={affidavitType}
              onChange={(event) => setAffidavitType(event.target.value as CandidateAffidavitType)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              <option value="odrh">ODR Housing</option>
              <option value="mhd">Mental Health Diversion</option>
            </select>
          </label>
          <Button id="odr-candidate-affidavit-generate" type="button" size="sm" onClick={() => void handleGenerate()} disabled={generating || (needsRequiredFields && (!diagnosis.trim() || !nextCourtDate))}>
            <FileText className="h-4 w-4" aria-hidden="true" />
            {generating ? 'Generating...' : 'Generate Affidavit'}
          </Button>
        </div>
      </div>

      {needsRequiredFields && (
        <div className="mb-3 grid gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm font-medium">
            Diagnosis
            <Textarea id="odr-candidate-affidavit-diagnosis" value={diagnosis} onChange={(event) => setDiagnosis(event.target.value)} rows={3} required disabled={generating} />
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium">
            Next Court Date
            <Input id="odr-candidate-affidavit-next-court-date" type="date" value={nextCourtDate} onChange={(event) => setNextCourtDate(event.target.value)} required disabled={generating} />
          </label>
        </div>
      )}

      {loading && <p className="text-xs text-[var(--neutral-gray-100)]">Loading affidavit history...</p>}
      {!loading && affidavits.length === 0 && (
        <p className="rounded-md bg-[var(--neutral-gray-10)] px-3 py-2 text-xs text-[var(--neutral-gray-100)]">
          No generated affidavits.
        </p>
      )}
      {affidavits.length > 0 && (
        <div className="space-y-2">
          {affidavits.map((affidavit, index) => (
            <div
              key={affidavit.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[var(--neutral-gray-40)] px-3 py-2"
            >
              <div className="text-xs text-[var(--neutral-gray-100)]">
                <div className="font-medium text-[var(--neutral-gray-120)]">{affidavit.fileName}</div>
                <div>
                  Generated {formatDateTime(affidavit.generatedAt)}
                  {affidavit.generatedByUsername ? ` by ${affidavit.generatedByUsername}` : ''}
                </div>
                <div>
                  {affidavit.sentAt
                    ? `Sent ${formatDateTime(affidavit.sentAt)}${affidavit.sentByUsername ? ` by ${affidavit.sentByUsername}` : ''}`
                    : 'Not sent'}
                </div>
              </div>
              <div className="flex flex-wrap items-end justify-end gap-2">
                {index === 0 && !affidavit.sentAt && (
                  <label className="flex min-w-[16rem] flex-col gap-1 text-xs font-medium text-[var(--neutral-gray-100)]">
                    Recipient email
                    <Input
                      id="odr-candidate-affidavit-recipient"
                      type="email"
                      value={recipient}
                      onChange={(event) => setRecipient(event.target.value)}
                      placeholder="name@example.org"
                      disabled={sendingEmail}
                    />
                  </label>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleDownload(affidavit)}
                  disabled={downloadingId === affidavit.id}
                >
                  {downloadingId === affidavit.id ? 'Downloading...' : 'Download'}
                </Button>
                {index === 0 && !affidavit.sentAt && (
                  <>
                    <Button
                      id="odr-candidate-affidavit-send-email"
                      type="button"
                      size="sm"
                      onClick={() => void handleSendEmail(affidavit)}
                      disabled={sendingEmail || !recipient.trim()}
                    >
                      {sendingEmail ? 'Sending...' : 'Send Email'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSentDate(localIsoDate());
                        setSentAffidavit(affidavit);
                      }}
                      disabled={sendingEmail}
                    >
                      Mark Sent
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!sentAffidavit} onOpenChange={(open) => !open && setSentAffidavit(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark affidavit sent</AlertDialogTitle>
            <AlertDialogDescription>
              Record the date the newest affidavit version was sent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label className="flex flex-col gap-1 text-sm font-medium">
            Sent date
            <Input type="date" value={sentDate} onChange={(event) => setSentDate(event.target.value)} />
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={markingSent}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(event) => { event.preventDefault(); void handleMarkSent(); }} disabled={markingSent || !sentDate}>
              {markingSent ? 'Saving...' : 'Mark Sent'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
